import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Modal form for creating/editing a rich-shape persona (Option Y schema).
 *
 *   context           - who is the analyst, what facility/setting?
 *   task              - bullet list of what to look for
 *   results_template  - JSON shape of one scene's output
 *   general           - cross-cutting rules (timestamps, JSON-only, etc)
 *
 * Submits via `useUpsertPersona`; on success closes and refreshes the
 * persona list query.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Save } from 'lucide-react';
import { useUpsertPersona } from '@/hooks/queries';
const EMPTY_TEMPLATE = `{
  "start_timestamp": "0.0s",
  "end_timestamp": "30.0s",
  "summary": "",
  "key_events": [],
  "critical": "false"
}`;
export function PersonaBuilder({ initial, onClose }) {
    const upsert = useUpsertPersona();
    const [name, setName] = useState(initial?.name ?? '');
    const [context, setContext] = useState(initial?.context ?? initial?.description ?? '');
    const [task, setTask] = useState(initial?.task ?? initial?.prompt ?? '');
    const [resultsTemplate, setResultsTemplate] = useState(initial?.results_template ?? EMPTY_TEMPLATE);
    const [general, setGeneral] = useState(initial?.general ?? '');
    const [templateError, setTemplateError] = useState(null);
    useEffect(() => {
        if (!resultsTemplate.trim()) {
            setTemplateError(null);
            return;
        }
        try {
            JSON.parse(resultsTemplate);
            setTemplateError(null);
        }
        catch (e) {
            setTemplateError(e.message);
        }
    }, [resultsTemplate]);
    const submit = async () => {
        if (!name.trim()) {
            toast.error('Name is required');
            return;
        }
        if (templateError) {
            toast.error('results_template is not valid JSON');
            return;
        }
        try {
            await upsert.mutateAsync({
                name: name.trim(),
                context: context.trim(),
                task: task.trim(),
                results_template: resultsTemplate.trim(),
                general: general.trim(),
            });
            toast.success(`Persona "${name}" saved`);
            onClose();
        }
        catch (e) {
            toast.error(e.message);
        }
    };
    return (_jsx("div", { role: "dialog", style: {
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }, onClick: onClose, children: _jsxs("div", { className: "card", style: { width: 'min(720px, 92vw)', maxHeight: '90vh', overflow: 'auto', background: 'var(--ey-dark)' }, onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "card-header", style: { display: 'flex', justifyContent: 'space-between' }, children: [_jsx("span", { className: "card-title", children: initial ? 'Edit Persona' : 'New Persona' }), _jsx("button", { className: "btn btn-sm", onClick: onClose, children: _jsx(X, { size: 14 }) })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Name *" }), _jsx("input", { value: name, onChange: (e) => setName(e.target.value), placeholder: "e.g. Warehouse Safety Inspector", disabled: !!initial })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Context \u2014 who is analyzing what, in what setting" }), _jsx("textarea", { rows: 3, value: context, onChange: (e) => setContext(e.target.value), placeholder: "Safety Inspector for an underground parking garage, responsible for fire-code compliance..." })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "Task \u2014 bullet list of what the analyst should look for" }), _jsx("textarea", { rows: 8, value: task, onChange: (e) => setTask(e.target.value), placeholder: '- Identify fire code violations (e.g., blocked exits, missing signage)\n- Detect electrical hazards (e.g., exposed wiring, overloaded circuits)' })] }), _jsxs("div", { className: "form-group", children: [_jsxs("label", { children: ["Results Template \u2014 JSON shape of one scene's output", templateError && (_jsxs("span", { style: { color: '#ff4d4f', marginLeft: 8, fontSize: 12 }, children: ["(", templateError, ")"] }))] }), _jsx("textarea", { rows: 10, value: resultsTemplate, onChange: (e) => setResultsTemplate(e.target.value), style: { fontFamily: 'monospace', fontSize: 12 } })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { children: "General \u2014 cross-cutting rules (e.g. JSON-only, timestamp format)" }), _jsx("textarea", { rows: 3, value: general, onChange: (e) => setGeneral(e.target.value), placeholder: "Always return valid JSON. Use [Xs-Ys] for timestamps. Don't speculate." })] }), _jsxs("div", { className: "btn-group", style: { justifyContent: 'flex-end' }, children: [_jsx("button", { className: "btn", onClick: onClose, children: "Cancel" }), _jsxs("button", { className: "btn btn-primary", onClick: submit, disabled: upsert.isPending || !!templateError, children: [_jsx(Save, { size: 14 }), " ", upsert.isPending ? 'Saving…' : 'Save Persona'] })] })] }) }));
}
